import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useRef, useState } from "preact/hooks";
import Modal, { type WizardElement } from "./components/Modal.tsx";
import { useQueryClient } from "@tanstack/preact-query";
import useApi from "./tests-api.ts";
import * as stylex from "@stylexjs/stylex";
import Button from "./components/Button.tsx";
import Spinner from "./components/Spinner.tsx";
import Form from "./components/Form.tsx";

import polychromeSingle from "./assets/polychrome.png";
import polychromeMany from "./assets/polychrome_many.png";
import suipiss from "./assets/suipiss.mp4";

const styles = stylex.create({
    shopHeader: {
        textAlign: "center",
        fontFamily: "'Inter Variable', sans-serif",
        fontWeight: 550,
        fontSize: 16,
        marginBottom: 20,
    },
    shopContainer: {
        gap: 20,
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter Variable', sans-serif",
        fontSize: 14,
    },
    shopEntry: {
        gap: 10,
        display: "grid",
        gridTemplateColumns: "22px repeat(3, 1fr)",
        alignItems: "center",
    },
    shopHint: {
        padding: 8,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        backgroundColor: "#EFF3F7",
        color: "#4B77A8",
        borderStyle: "solid",
        borderWidth: 1,
    },
    shopResult: {
        height: "100%",
        display: "grid",
        gridTemplateRows: "24px 100px",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        fontFamily: "'Inter Variable', sans-serif",
        fontSize: 14,
    },
    activatorHeader: {
        fontFamily: "'Inter Variable', sans-serif",
        textAlign: "center",
        fontSize: 16,
        color: "#666",
    },
    activatorContainer: {
        alignItems: "center",
    },
    activatorSuipiss: {
        width: "80%",
    },
});

const sleep: (t: number) => Promise<void> = (t) => new Promise((r) => setTimeout(r, t));

const EditorUnlockModal = NiceModal.create(() => {
    let password: string = "";
    const modal = useModal();
    const wizard = useRef<WizardElement>(null);
    const queryClient = useQueryClient();
    const onStepChanged = async (step: number) => {
        if (step != 1) return;

        try {
            await sleep(600);
            await (await useApi()).activateEditor(password);
            await queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
            modal.resolve(true);
            modal.remove();
        } catch (e) {
            wizard.current!.advance();
        }
    };

    return (
        <Modal.Wizard
            ref={wizard}
            closable={true}
            onStepChanged={onStepChanged}
            steps={[
                ({ advance }) => (
                    <div {...stylex.props(styles.shopContainer, styles.activatorContainer)}>
                        <span {...stylex.props(styles.activatorHeader)}>
                            Для активации редактора нужно ввести кодовое слово. Его можно узнать тут
                            -&gt;&nbsp;
                            <a target="_blank" href="https://openvk.org/minitest">
                                https://openvk.org/minitest
                            </a>
                        </span>
                        <video
                            {...stylex.props(styles.activatorSuipiss)}
                            src={suipiss}
                            autoplay
                            loop
                        ></video>
                        <Form.Input
                            style={{ fontSize: 18, width: "80%", textAlign: "center" }}
                            type="password"
                            placeholder="Введите кодовое слово"
                            onChange={(e) => (password = e.currentTarget.value)}
                        />
                        <Button label="Активировать редактор" onClick={advance} />
                    </div>
                ),
                () => <Spinner.Loader label="Активация редактора" fullHeight={true} />,
                ({ setStep, resolve }) => (
                    <div {...stylex.props(styles.shopResult)}>
                        <span style={{ fontWeight: 500, textAlign: "center" }}>
                            Пароль не подошёл...
                        </span>
                        <div style={{ textAlign: "center" }}>
                            <Button label="Повторить попытку" onClick={() => setStep(0)} />
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button label="Отменить" kind="danger" onClick={() => resolve(null)} />
                        </div>
                    </div>
                ),
            ]}
        />
    );
});

const GetKromer = NiceModal.create(() => {
    let amount: number = 0;
    let [success, setSuccess] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const wizard = useRef<WizardElement>(null);
    const onStepChanged = async (step: number) => {
        if (step != 1) return;

        try {
            await sleep(600);
            // @ts-ignore
            const payment = await VKAPI.buy(amount / 25, `Полихром (${amount}шт.)`);
            await (await useApi()).buyKromer(payment["outSum"], payment["signature"]);
            await queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
            setSuccess(true);
        } catch (e) {
            setSuccess(false);
        }

        await sleep(200);
        wizard.current!.advance();
    };

    return (
        <Modal.Wizard
            ref={wizard}
            closable={true}
            onStepChanged={onStepChanged}
            steps={[
                ({ advance }) => (
                    <>
                        <div {...stylex.props(styles.shopHeader)}>Магазин полихрома</div>
                        <div {...stylex.props(styles.shopContainer)}>
                            <div {...stylex.props(styles.shopEntry)}>
                                <img
                                    src={polychromeSingle}
                                    alt="25 полихромов"
                                    style={{ width: "22px" }}
                                />
                                <div style={{ textAlign: "center" }}>25 полихромов</div>
                                <div style={{ textAlign: "center" }}>
                                    <span style={{ color: "#4B77A8" }}>1 голос</span>
                                </div>
                                <div>
                                    <Button
                                        label="Купить"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            amount = 25;
                                            advance();
                                        }}
                                    />
                                </div>
                            </div>
                            <div {...stylex.props(styles.shopEntry)}>
                                <img
                                    src={polychromeMany}
                                    alt="100 полихромов"
                                    style={{ width: "22px" }}
                                />
                                <div style={{ textAlign: "center" }}>100 полихромов</div>
                                <div style={{ textAlign: "center" }}>
                                    <span style={{ color: "#4B77A8" }}>4 голоса</span>
                                </div>
                                <div>
                                    <Button
                                        label="Купить"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            amount = 100;
                                            advance();
                                        }}
                                    />
                                </div>
                            </div>
                            <div {...stylex.props(styles.shopHint)}>
                                Заходите в приложение почаще &mdash; 10 полихромов выдаются
                                бесплатно каждый день за открытие мини тестов!
                            </div>
                        </div>
                    </>
                ),
                () => <Spinner.Loader label="Обработка платежа" fullHeight={true} />,
                ({ setStep, resolve }) =>
                    success ? (
                        <div {...stylex.props(styles.shopResult)}>
                            <span style={{ fontWeight: 500, textAlign: "center" }}>
                                Оплата прошла успешно!
                            </span>
                            <div style={{ textAlign: "center" }}>
                                <Button
                                    label="Хорошо"
                                    kind="success"
                                    onClick={() => resolve(true)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div {...stylex.props(styles.shopResult)}>
                            <span style={{ fontWeight: 500, textAlign: "center" }}>
                                Платёж не прошёл...
                            </span>
                            <div style={{ textAlign: "center" }}>
                                <Button label="Повторить попытку" onClick={() => setStep(0)} />
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                <Button
                                    label="Отменить"
                                    kind="danger"
                                    onClick={() => resolve(null)}
                                />
                            </div>
                        </div>
                    ),
            ]}
        />
    );
});

export default { EditorUnlockModal, GetKromer };
